import React from "react";
import { ArrowUpRight, ShieldCheck, Sparkles, Info } from "lucide-react";

import SectionLabel from "./SectionLabel";
import { ImageSlot } from "./ImageSlot";
import {
  CASH_IMPACT_LABEL, OPPS_COUNT, ROI_MULTIPLIER, NET_MARGIN_PCT,
  PROFIT_TODAY_USD, ORDERS_PER_DAY, AD_SPEND_USD, REVENUE_VELOCITY_USD,
  GROWTH_SCORE, GROWTH_PILLARS, fmtUsdK,
} from "./aigoMetrics";

/**
 * Gauge — arc rendered with SVG. Shows a value and label.
 * Used for ROI and Net Margin headline gauges.
 */
const Gauge = ({ value, max, suffix, label, tone = "emerald", testid }) => {
  const pct = Math.max(0, Math.min(1, value / max));
  const start = -220;
  const end = 40;
  const angle = start + (end - start) * pct;
  const rad = (a) => (a * Math.PI) / 180;
  const x1 = 100 + 80 * Math.cos(rad(start));
  const y1 = 100 + 80 * Math.sin(rad(start));
  const x2 = 100 + 80 * Math.cos(rad(angle));
  const y2 = 100 + 80 * Math.sin(rad(angle));
  const largeArc = end - start > 180 && pct > 0.5 ? 1 : 0;
  const stroke = tone === "amber" ? "#d97706" : "#059669";
  const track = "#e2e8f0";
  const glow = tone === "amber" ? "rgba(217,119,6,0.25)" : "rgba(16,185,129,0.28)";

  return (
    <div data-testid={testid} className="flex flex-col items-center">
      <div className="relative w-40 h-32 sm:w-52 sm:h-40">
        <svg viewBox="0 0 200 140" className="w-full h-full">
          {/* Track */}
          <path
            d={`M ${100 + 80 * Math.cos(rad(start))} ${100 + 80 * Math.sin(rad(start))}
                A 80 80 0 1 1 ${100 + 80 * Math.cos(rad(end))} ${100 + 80 * Math.sin(rad(end))}`}
            fill="none" stroke={track} strokeWidth="14" strokeLinecap="round"
          />
          {/* Value arc */}
          <path
            d={`M ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2}`}
            fill="none" stroke={stroke} strokeWidth="14" strokeLinecap="round"
            style={{ filter: `drop-shadow(0 4px 12px ${glow})` }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pt-4">
          <div className={`text-3xl sm:text-4xl font-extrabold tabular-nums tracking-tight ${
            tone === "amber" ? "text-amber-600" : "text-emerald-700"
          }`}>
            {value}{suffix}
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mt-1">
            {label}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatTile = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">{label}</div>
    <div className="mt-1 text-slate-900 text-2xl md:text-3xl font-extrabold tabular-nums tracking-tight">
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-emerald-700 font-semibold">{sub}</div>}
  </div>
);

export const Proof = () => (
  <section
    id="proof"
    data-testid="proof-section"
    className="px-6 md:px-10 py-20 md:py-24 border-t border-slate-100 bg-slate-50"
  >
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12 md:mb-14">
        <SectionLabel>Example dashboard · not a guarantee</SectionLabel>
        <h2 className="text-3xl md:text-5xl lg:text-6xl text-slate-900 max-w-4xl mx-auto mt-4 mb-4 tracking-tight font-bold leading-[1.05]">
          <span className="text-emerald-600">{CASH_IMPACT_LABEL}</span> in cash impact ready when you are.
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
          A snapshot of one connected merchant. <strong className="text-slate-900">{OPPS_COUNT} opportunities</strong> queued and ranked by $ impact. All visible on Free — capture with Pro.
        </p>
        <div className="mt-3 text-xs text-slate-500 inline-flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5" /> Numbers taken from the live platform demo. Your results will differ.
        </div>
      </div>

      {/* Gauges + live metrics */}
      <div className="grid lg:grid-cols-3 gap-6 md:gap-8 items-center max-w-6xl mx-auto">
        {/* ROI gauge */}
        <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm flex flex-col items-center">
          <Gauge value={ROI_MULTIPLIER.toFixed(2)} max={10} suffix="x" label="ROI · last 30d" testid="proof-gauge-roi" />
          <div className="mt-2 text-xs text-slate-500 text-center max-w-xs">
            Return on ad spend after fees. Updated every 15 mins.
          </div>
        </div>

        {/* Cash impact center card */}
        <div
          data-testid="proof-cash-card"
          className="rounded-3xl border border-emerald-200 bg-white p-6 md:p-8 shadow-xl shadow-emerald-500/10 ring-1 ring-emerald-100 relative"
        >
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] tracking-[0.16em] uppercase font-bold border border-emerald-200">
            <Sparkles className="w-3 h-3" /> Approve-ready
          </div>
          <div className="mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tabular-nums tracking-tight text-emerald-700">
            {CASH_IMPACT_LABEL}
          </div>
          <div className="mt-2 text-slate-700 text-sm md:text-base leading-relaxed">
            across <strong className="text-slate-900">{OPPS_COUNT} opportunities</strong> — ranked by $ impact and confidence. On Pro, one tap approves them all.
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-slate-500">Growth Score</div>
              <div className="text-slate-900 text-2xl font-extrabold tabular-nums tracking-tight">{GROWTH_SCORE}<span className="text-slate-400 text-sm">/100</span></div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-slate-500">6 pillars</div>
              <div className="text-slate-900 text-2xl font-extrabold tabular-nums tracking-tight">
                {GROWTH_PILLARS.filter((p) => p.score >= 75).length}<span className="text-slate-400 text-sm">/6 healthy</span>
              </div>
            </div>
          </div>
        </div>

        {/* Net Margin gauge */}
        <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm flex flex-col items-center">
          <Gauge value={NET_MARGIN_PCT} max={100} suffix="%" label="Net Margin" testid="proof-gauge-margin" />
          <div className="mt-2 text-xs text-slate-500 text-center max-w-xs">
            Contribution margin after ad spend + refunds + fees.
          </div>
        </div>
      </div>

      {/* Live metrics row */}
      <div className="mt-8 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-6xl mx-auto">
        <StatTile testid="proof-stat-profit"  label="Profit today"      value={fmtUsdK(PROFIT_TODAY_USD)}      sub="green today" />
        <StatTile testid="proof-stat-orders"  label="Orders / day"      value={ORDERS_PER_DAY} />
        <StatTile testid="proof-stat-spend"   label="Ad spend"          value={fmtUsdK(AD_SPEND_USD)} />
        <StatTile testid="proof-stat-vel"     label="Revenue velocity"  value={fmtUsdK(REVENUE_VELOCITY_USD)} sub="last 24h" />
      </div>

      {/* Command Center full snapshot — reuses /screenshots/hero.png */}
      <div className="mt-10 md:mt-12 max-w-5xl mx-auto">
        <ImageSlot
          slotId="proof-command-center"
          src="/screenshots/hero.png"
          label="Command Center · Growth Score + $-Leaks + Approve-all-safe"
          aspect="16/9"
          accent
        />
      </div>

      {/* Safety line */}
      <div className="mt-8 text-center text-sm text-slate-500 inline-flex items-center gap-2 w-full justify-center">
        <ShieldCheck className="w-4 h-4 text-emerald-600" />
        Nothing ships to your store without your explicit approval · undo any implementation.
      </div>
    </div>
  </section>
);

export default Proof;
